import type PlayClient from '@empathybroker/tsclient-play'
import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type StatsClient from '@empathybroker/tsclient-stats'
import type {
  AccessTokenProvider,
  DateTimeAdapter,
  InstanceFiltersProvider,
  InstanceProvider,
  Promotion,
  PromotionAdapter,
  QueryableAdapter,
  QueryRankingToolConfigProvider,
  UserToolConfigProvider,
} from '@empathyco/playboard-core-domain'
import type { FeatureFlagsConfig } from '@empathyco/playboard-core-domain/dist/models'
import type { SessionService } from '@empathyco/x-utils'
import type { interfaces } from 'inversify'
import type { Store } from 'vuex'
import type {
  BrowseFacetAdapter,
  FacetsInfoAdapter,
  FieldValueAdapter,
  IndexClientAdapter,
  IndexJobAdapter,
  ToolReportingAdapter,
} from '../adapter'
import type { AppConfig, Dictionary, FeaturesConfig, ServicesConfig } from '../models'
import type { BrowseService } from '../modules/browse'
import type { EqualizeAdapter, EqualizeCrudService } from '../modules/equalize'
import type { PromotionCrudService, PromotionFactory } from '../modules/promotion'
import type { SearchService } from '../modules/search'
import type {
  AuditAdapterProvider,
  AuditObjectTypeConfigProvider,
  CalendarConfigProvider,
  ContextProvider,
  CrudServiceProvider,
  DomainServiceProvider,
  EqualizeFieldNamesProvider,
  FeatureConfigProvider,
  IndexClientProvider,
  InstancesProvider,
  KpisConfigProvider,
  PromotionConfigProvider,
  RolesProvider,
  TaggingBannerConfigProvider,
  ToolConfigProvider,
} from '../providers'
import type {
  AuthTokenService,
  BrowseFacetService,
  CurationToolDomainService,
  EqualizeDomainService,
  FacetsDomainService,
  FacetsInfoCrudService,
  FieldValuesService,
  IAuditService,
  IndexClientService,
  IndexJobService,
  InstanceSearchManagementService,
  IObjectIdService,
  NextQueriesDomainService,
  PlayServiceCrudWrapper,
  PromotionDomainService,
  QueryRankingDomainService,
  RelatedPromptsPreviewService,
  RelatedTagsDomainService,
  SearchToolingService,
  StorageService,
  TaggingService,
  ToolReportingService,
} from '../services'
import {
  authContainerModule,
  configContainerModule,
  curationToolContainerModule,
  facetsContainerModule,
  PlatformDateTimeAdapter,
  PlatformQueryableAdapter,
  productContainerModule,
  queryProcessorContainerModule,
  queryRankingContainerModule,
  relatedPromptsContainerModule,
  searchContainerModule,
  usersContainerModule,
} from '@empathyco/playboard-core-domain'
import { BrowserStorageService } from '@empathyco/x-storage-service'
import { DefaultSessionService } from '@empathyco/x-utils'
import { Container } from 'inversify'
import Vue from 'vue'
import Vuex from 'vuex'
import {
  PlatformBrowseFacetAdapter,
  PlatformFacetsInfoAdapter,
  PlatformFieldValueAdapter,
  PlatformIndexClientAdapter,
  PlatformIndexJobAdapter,
  PlatformToolReportingAdapter,
} from '../adapter'
import XSearchbox from '../components/x/x-searchbox.component.vue'
import { LocaleEnum } from '../enums'
import { PlatformBrowseService } from '../modules/browse'
import { PlatformEqualizeAdapter, PlatformEqualizeCrudService } from '../modules/equalize'
import {
  BasePromotionFactory,
  PlatformPromotionAdapter,
  PlatformPromotionCrudService,
} from '../modules/promotion'
import { PlatformSearchService } from '../modules/search'
import {
  getCrudServiceProvider,
  getFeatureConfigProvider,
  getInstanceFromStoreProvider,
  getInstancesFromInstanceManagementProvider,
  getInstancesFromPlayProvider,
  getKpisConfigProvider,
  getPlatformAuditAdapterProvider,
  getPlatformAuditObjectTypeConfigProvider,
  getPlatformCalendarConfigProvider,
  getPlatformDomainServiceProvider,
  getPlatformEqualizeFieldNamesProvider,
  getPlatformIndexClientProvider,
  getPlatformPromotionConfigProvider,
  getPlatformToolConfigProvider,
  getPlatformUserToolConfigProvider,
  getQueryRankingToolConfigFromStoreProvider,
  getRolesProvider,
  getStoreContextProvider,
  getStoreTokenProvider,
  getTaggingBannerConfigProvider,
} from '../providers'
import {
  IamAuthTokenService,
  ObjectIdService,
  PlatformBrowseFacetService,
  PlatformCurationToolDomainService,
  PlatformEqualizeDomainService,
  PlatformFacetsDomainService,
  PlatformFacetsInfoCrudService,
  PlatformFieldValuesService,
  PlatformIndexClientService,
  PlatformIndexJobService,
  PlatformNextQueriesDomainService,
  PlatformPlayServiceCrudWrapper,
  PlatformPromotionDomainService,
  PlatformQueryRankingDomainService,
  PlatformRelatedPromptsPreviewService,
  PlatformRelatedTagsDomainService,
  PlatformSearchToolingService,
  PlatformToolReportingService,
  PlayAuthTokenService,
  TaggingLocalService,
} from '../services'
import { LocalStorageService } from '../services/local-storage.service'
import { CONFIG_CONSTS } from '../store/config/config.consts'
import { URLUtils } from '../utils/url.utils'
import { TYPES } from './inversify.types'

export const CONTAINER = new Container()

CONTAINER.bind<Dictionary<Dictionary>>(TYPES.Messages).toConstantValue({
  [LocaleEnum.ENGLISH]: {},
})

CONTAINER.bind<StorageService>(TYPES.StorageService).toConstantValue(new LocalStorageService())

CONTAINER.bind<AuthTokenService>(TYPES.AuthTokenService).toDynamicValue(context => {
  // TODO Remove @ts-ignore once feature flag is available
  // @ts-ignore
  const { oidcLogin } = context.container.get<AppConfig>(TYPES.AppConfig)
  return oidcLogin ? new IamAuthTokenService() : new PlayAuthTokenService()
})

CONTAINER.bind<InstanceSearchManagementService | undefined>(
  TYPES.InstanceSearchManagementService,
).toConstantValue(undefined)

CONTAINER.bind<IAuditService | undefined>(TYPES.AuditService).toConstantValue(undefined)

CONTAINER.bind<SessionService>(TYPES.SessionService).toConstantValue(
  new DefaultSessionService(new BrowserStorageService(localStorage, 'ebp'), 0),
)
CONTAINER.bind<TaggingService>(TYPES.TaggingService).toConstantValue(new TaggingLocalService())

CONTAINER.bind<interfaces.Factory<StatsClient>>(TYPES.StatsClient).toFactory<
  StatsClient | undefined
>(() => (): StatsClient | undefined => undefined)
CONTAINER.bind<interfaces.Factory<PlayClient>>(TYPES.PlayClient).toFactory<PlayClient | undefined>(
  () => (): PlayClient | undefined => undefined,
)

Vue.use(Vuex)
CONTAINER.bind<Store<any>>(TYPES.Store).toConstantValue(new Vuex.Store({}))

CONTAINER.bind<FeaturesConfig>(TYPES.FeaturesConfig).toConstantValue({})

CONTAINER.bind<IObjectIdService>(TYPES.ObjectIdService).to(ObjectIdService).inSingletonScope()

CONTAINER.bind(TYPES.SemanticService).toConstantValue(undefined)

CONTAINER.bind<AccessTokenProvider>(TYPES.AccessTokenProvider).toDynamicValue(getStoreTokenProvider)

CONTAINER.bind<InstancesProvider>(TYPES.InstancesProvider).toDynamicValue(context => {
  const { loadInstancesFromInstanceManagement } = context.container.get<AppConfig>(TYPES.AppConfig)
  if (loadInstancesFromInstanceManagement) {
    return getInstancesFromInstanceManagementProvider(context)
  }
  return getInstancesFromPlayProvider(context)
})

CONTAINER.bind<ContextProvider>(TYPES.ContextProvider).toDynamicValue(getStoreContextProvider)

CONTAINER.bind<InstanceProvider>(TYPES.InstanceProvider).toDynamicValue(
  getInstanceFromStoreProvider,
)

CONTAINER.bind<FeatureConfigProvider>(TYPES.FeatureConfigProvider).toDynamicValue(
  getFeatureConfigProvider,
)

CONTAINER.bind<QueryableAdapter>(TYPES.QueryableAdapter).to(PlatformQueryableAdapter)
CONTAINER.bind<EqualizeAdapter>(TYPES.EqualizeAdapter).to(PlatformEqualizeAdapter)
CONTAINER.bind<BrowseFacetAdapter>(TYPES.BrowseFacetAdapter).to(PlatformBrowseFacetAdapter)
CONTAINER.bind<IndexClientAdapter>(TYPES.IndexClientAdapter).to(PlatformIndexClientAdapter)
CONTAINER.bind<IndexJobAdapter>(TYPES.IndexJobAdapter).to(PlatformIndexJobAdapter)
CONTAINER.bind<FacetsInfoAdapter>(TYPES.FacetsInfoAdapter).to(PlatformFacetsInfoAdapter)
CONTAINER.bind<AuditAdapterProvider>(TYPES.AuditAdapterProvider).toDynamicValue(
  getPlatformAuditAdapterProvider,
)

CONTAINER.bind<FieldValueAdapter>(TYPES.FieldValueAdapter).to(PlatformFieldValueAdapter)
CONTAINER.bind<ToolReportingAdapter>(TYPES.ToolReportingAdapter).to(PlatformToolReportingAdapter)

// Shared core adapter, previously bound as a side effect of loading the communications module
CONTAINER.bind<DateTimeAdapter>(TYPES.ApiDateTimeAdapter).to(PlatformDateTimeAdapter)

CONTAINER.bind<Partial<ServicesConfig>>(TYPES.ServicesConfig).toConstantValue({})
CONTAINER.bind<AppConfig | Record<string, never>>(TYPES.AppConfig).toConstantValue({})

CONTAINER.bind<CrudServiceProvider>(TYPES.CrudServiceProvider).toDynamicValue(
  getCrudServiceProvider,
)

CONTAINER.bind<EqualizeCrudService>(TYPES.EqualizeCrudService).to(PlatformEqualizeCrudService)

CONTAINER.bind<FacetsInfoCrudService>(TYPES.FacetsInfoCrudServide).to(PlatformFacetsInfoCrudService)

CONTAINER.bind<PlayServiceCrudWrapper>(TYPES.PlayServiceCrudWrapper).to(
  PlatformPlayServiceCrudWrapper,
)

CONTAINER.bind<SearchService>(TYPES.SearchService).to(PlatformSearchService)
CONTAINER.bind<BrowseService>(TYPES.BrowseService).to(PlatformBrowseService)

CONTAINER.bind<EqualizeDomainService>(TYPES.EqualizeDomainService).to(PlatformEqualizeDomainService)
CONTAINER.bind<CurationToolDomainService>(TYPES.CurationToolDomainService).to(
  PlatformCurationToolDomainService,
)
CONTAINER.bind<NextQueriesDomainService>(TYPES.NextQueriesDomainService).to(
  PlatformNextQueriesDomainService,
)

CONTAINER.bind<RelatedTagsDomainService>(TYPES.RelatedTagsDomainService).to(
  PlatformRelatedTagsDomainService,
)

CONTAINER.bind<BrowseFacetService>(TYPES.BrowseFacetService).to(PlatformBrowseFacetService)

CONTAINER.bind<IndexClientService>(TYPES.IndexClientService).to(PlatformIndexClientService)
CONTAINER.bind<IndexJobService>(TYPES.IndexJobService).to(PlatformIndexJobService)

CONTAINER.bind<SearchToolingService>(TYPES.SearchToolingService).to(PlatformSearchToolingService)
CONTAINER.bind<RelatedPromptsPreviewService>(TYPES.RelatedPromptsPreviewService).to(
  PlatformRelatedPromptsPreviewService,
)
CONTAINER.bind<ToolReportingService>(TYPES.ToolReportingService).to(PlatformToolReportingService)

CONTAINER.bind<ToolConfigProvider>(TYPES.ToolConfigProvider).toDynamicValue(
  getPlatformToolConfigProvider,
)

CONTAINER.bind<DomainServiceProvider>(TYPES.DomainServiceProvider).toDynamicValue(
  getPlatformDomainServiceProvider,
)

CONTAINER.bind<IndexClientProvider>(TYPES.IndexClientProvider).toDynamicValue(
  getPlatformIndexClientProvider,
)

CONTAINER.bind<AuditObjectTypeConfigProvider>(TYPES.AuditObjectTypeConfigProvider).toDynamicValue(
  getPlatformAuditObjectTypeConfigProvider,
)

CONTAINER.bind<TaggingBannerConfigProvider>(TYPES.TaggingBannerConfigProvider).toDynamicValue(
  getTaggingBannerConfigProvider,
)

CONTAINER.bind<KpisConfigProvider>(TYPES.KpisConfigProvider).toDynamicValue(getKpisConfigProvider)
CONTAINER.bind<RolesProvider>(TYPES.RolesProvider).toDynamicValue(getRolesProvider)

CONTAINER.bind<FieldValuesService>(TYPES.FieldValuesService).to(PlatformFieldValuesService)

CONTAINER.bind<EqualizeFieldNamesProvider>(TYPES.EqualizeFieldNamesProvider).toDynamicValue(
  getPlatformEqualizeFieldNamesProvider,
)

CONTAINER.bind<CalendarConfigProvider>(TYPES.CalendarConfigProvider).toDynamicValue(
  getPlatformCalendarConfigProvider,
)

CONTAINER.bind<string>(TYPES.ConfigUrl).toDynamicValue(({ container }) =>
  URLUtils.extractServiceUrl(
    container.get<Partial<ServicesConfig>>(TYPES.ServicesConfig)?.configuration,
  ),
)
CONTAINER.load(configContainerModule)

CONTAINER.load(authContainerModule)

CONTAINER.bind<string>(TYPES.EqualizeUrl).toDynamicValue(({ container }) =>
  URLUtils.extractServiceUrl(container.get<Partial<ServicesConfig>>(TYPES.ServicesConfig)?.search),
)

CONTAINER.bind<string>(TYPES.IndexUrl).toDynamicValue(({ container }) =>
  URLUtils.extractServiceUrl(container.get<Partial<ServicesConfig>>(TYPES.ServicesConfig)?.index),
)

CONTAINER.bind<string>(TYPES.QueryProcessorUrl).toDynamicValue(({ container }) =>
  URLUtils.extractServiceUrl(
    container.get<Partial<ServicesConfig>>(TYPES.ServicesConfig)?.queryProcessor,
  ),
)
CONTAINER.load(queryProcessorContainerModule)

CONTAINER.bind<string>(TYPES.SearchUrl).toDynamicValue(({ container }) =>
  URLUtils.extractServiceUrl(container.get<Partial<ServicesConfig>>(TYPES.ServicesConfig)?.search),
)
CONTAINER.load(searchContainerModule)

CONTAINER.bind<string>(TYPES.StatsUrl).toDynamicValue(({ container }) =>
  URLUtils.extractServiceUrl(container.get<Partial<ServicesConfig>>(TYPES.ServicesConfig)?.stats),
)

CONTAINER.bind<QueryRankingToolConfigProvider>(TYPES.QueryRankingToolConfigProvider).toDynamicValue(
  getQueryRankingToolConfigFromStoreProvider,
)
CONTAINER.bind<string>(TYPES.QueryRankingUrl).toDynamicValue(({ container }) =>
  URLUtils.extractServiceUrl(container.get<Partial<ServicesConfig>>(TYPES.ServicesConfig)?.search),
)
CONTAINER.load(queryRankingContainerModule)
CONTAINER.bind<QueryRankingDomainService>(TYPES.QueryRankingDomainService).to(
  PlatformQueryRankingDomainService,
)

CONTAINER.bind<string>(TYPES.RelatedPromptsUrl).toDynamicValue(({ container }) =>
  URLUtils.extractServiceUrl(container.get<Partial<ServicesConfig>>(TYPES.ServicesConfig)?.beacon),
)
CONTAINER.bind<string>(TYPES.RelatedPromptsPreviewUrl).toDynamicValue(({ container }) =>
  URLUtils.extractServiceUrl(container.get<Partial<ServicesConfig>>(TYPES.ServicesConfig)?.search),
)
CONTAINER.load(relatedPromptsContainerModule)

CONTAINER.bind<string>(TYPES.UsersUrl).toDynamicValue(
  ({ container }) =>
    container.get<Partial<ServicesConfig>>(TYPES.ServicesConfig).oidc?.authority ?? '',
)
CONTAINER.bind<UserToolConfigProvider>(TYPES.UserToolConfigProvider).toDynamicValue(
  getPlatformUserToolConfigProvider,
)
CONTAINER.load(usersContainerModule)

// modules/facets
CONTAINER.bind<string>(TYPES.FacetsUrl).toDynamicValue(({ container }) =>
  URLUtils.extractServiceUrl(container.get<Partial<ServicesConfig>>(TYPES.ServicesConfig)?.search),
)
CONTAINER.load(facetsContainerModule)
CONTAINER.bind<FacetsDomainService>(TYPES.FacetsDomainService).to(PlatformFacetsDomainService)

// modules/promotion
CONTAINER.bind<PromotionAdapter>(TYPES.PromotionAdapter).to(PlatformPromotionAdapter)
CONTAINER.bind<PromotionCrudService>(TYPES.PromotionCrudService).to(PlatformPromotionCrudService)
CONTAINER.bind<PromotionDomainService>(TYPES.PromotionDomainService).to(
  PlatformPromotionDomainService,
)
CONTAINER.bind<PromotionFactory>(TYPES.PromotionFactory).toFactory<
  Promotion,
  Parameters<PromotionFactory>
>((): PromotionFactory => BasePromotionFactory)

CONTAINER.bind<PromotionConfigProvider>(TYPES.PromotionConfigProvider).toDynamicValue(
  getPlatformPromotionConfigProvider,
)

// modules/tooling-aggregator
CONTAINER.bind<string>(TYPES.ToolingAggregatorUrl).toDynamicValue(({ container }) =>
  URLUtils.extractServiceUrl(
    container.get<Partial<ServicesConfig>>(TYPES.ServicesConfig)?.toolingAggregator,
  ),
)

// modules/curation-tool
CONTAINER.bind<string>(TYPES.CurationToolsUrl).toDynamicValue(({ container }) =>
  URLUtils.extractServiceUrl(container.get<Partial<ServicesConfig>>(TYPES.ServicesConfig)?.beacon),
)
CONTAINER.bind<InstanceFiltersProvider>(TYPES.InstanceFiltersProvider).toDynamicValue(
  ({ container }) => {
    const store = container.get<Store<any>>(TYPES.Store)
    const instance: Instance =
      store.getters[`${CONFIG_CONSTS.moduleName}/${CONFIG_CONSTS.getters.currentInstance}`]

    return () => (instance?.filters ?? {}) as ReturnType<InstanceFiltersProvider>
  },
)
CONTAINER.bind<FeatureFlagsConfig>(TYPES.FeatureFlags).toDynamicValue(({ container }) => {
  const featureflags = container.get<AppConfig>(TYPES.AppConfig)

  return featureflags
})
CONTAINER.load(curationToolContainerModule)

CONTAINER.bind<typeof XSearchbox>(TYPES.XSearchBoxComponent).toConstantValue(XSearchbox)
CONTAINER.bind(TYPES.GetSearchExtraParametersProvider).toConstantValue(undefined)

CONTAINER.load(productContainerModule)
